import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { GuardianAttendanceApi } from '@/services/guardian-attendance.api';
import { SchoolsApi } from '@/services/superadmin.api';
import { OrgPortalApi } from '@/services/org.api';
import type { ExportFormat } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Input, Select } from '@/components/ui/Input';

/** Matches the old system's "Parent Attendance Summary" screen. */
export function ParentAttendanceSummaryPage({ scope }: { scope: 'superadmin' | 'organization' | 'school' }) {
  const showSchoolPicker = scope !== 'school';
  const [schoolId, setSchoolId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const { data: schools = [] } = useQuery({
    queryKey: ['schools-for-parent-attendance-summary', scope],
    queryFn: async () =>
      scope === 'organization' ? (await OrgPortalApi.listSchools({ limit: 100 })).items : (await SchoolsApi.list({ limit: 100 })).items,
    enabled: showSchoolPicker,
  });

  // Same "default to a populated period" rule as student attendance — data
  // is dead since 2025, so seed the range from the most recent date that
  // actually has data (that whole month), not "today".
  const { data: latest } = useQuery({
    queryKey: ['guardian-attendance-latest-for-summary', schoolId],
    queryFn: () => GuardianAttendanceApi.list({ limit: 1, schoolId: showSchoolPicker ? schoolId || undefined : undefined }),
  });

  useEffect(() => {
    const latestDate = latest?.items[0]?.date;
    if (latestDate) {
      setEndDate((d) => d || latestDate);
      setStartDate((d) => d || dayjs(latestDate).startOf('month').format('YYYY-MM-DD'));
    }
  }, [latest]);

  const { data: rows = [], isFetching } = useQuery({
    queryKey: ['guardian-attendance-summary', schoolId, startDate, endDate],
    queryFn: () =>
      GuardianAttendanceApi.summary({
        schoolId: showSchoolPicker ? schoolId || undefined : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      }),
    enabled: !!startDate && !!endDate,
  });

  const runExport = async (format: ExportFormat) => {
    if (busy) return;
    setBusy(format);
    try {
      await GuardianAttendanceApi.exportSummary(format, {
        schoolId: showSchoolPicker ? schoolId || undefined : undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <PageHeader
        title="Parent Attendance Summary"
        description="Present/absent totals per guardian over a date range."
        actions={
          <div className="inline-flex overflow-hidden rounded-md border border-slate-200">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('xlsx')}
              disabled={!!busy}
              title="Export to Excel"
            >
              {busy === 'xlsx' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-green-600" />}
              Excel
            </button>
            <div className="w-px bg-slate-200" />
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => runExport('pdf')}
              disabled={!!busy}
              title="Export to PDF"
            >
              {busy === 'pdf' ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-600" />}
              PDF
            </button>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {showSchoolPicker && (
          <Select className="!w-56" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
            <option value="">All schools</option>
            {schools.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        )}
        <Input type="date" className="!w-40" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" className="!w-40" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
      </div>

      {isFetching ? (
        <div className="card p-8 text-center text-slate-500">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card p-8 text-center text-slate-500">
          No guardian attendance recorded for the selected range.
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Guardian</th>
                {showSchoolPicker && <th className="px-4 py-3">School</th>}
                <th className="px-4 py-3">Present</th>
                <th className="px-4 py-3">Absent</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={`${r.guardianId}-${r.schoolId}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{r.guardianName}</td>
                  {showSchoolPicker && <td className="px-4 py-2.5 text-slate-600">{r.schoolName}</td>}
                  <td className="px-4 py-2.5">
                    <Badge tone="green">{r.present}</Badge>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge tone="red">{r.absent}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-slate-600">{r.total}</td>
                  <td className="px-4 py-2.5">{r.notMarked && <Badge tone="slate">Not Marked</Badge>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
